import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import type { InvoiceDto } from '@/types/billing';
import { isWithinDateRange } from '@/lib/dateRangeFilter';
import PortalDateRangeFilter from './PortalDateRangeFilter';

async function downloadInvoicePdf(invoice: InvoiceDto) {
  const response = await apiClient.get(`/client-portal/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${invoice.invoiceNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function ClientPortalInvoicesPage() {
  const { t } = useTranslation('clientPortal');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const { data: rawRows = [], isLoading } = useQuery({
    queryKey: ['client-portal', 'invoices'],
    queryFn: async () => (await apiClient.get<InvoiceDto[]>('/client-portal/invoices')).data,
  });
  const rows = useMemo(
    () => rawRows.filter((r) => isWithinDateRange(r.invoiceDate, fromDate, toDate)),
    [rawRows, fromDate, toDate],
  );

  const columns: DataTableColumn<InvoiceDto>[] = [
    { field: 'invoiceNumber', headerName: t('invoicesPage.table.invoiceNumber') },
    { field: 'invoiceDate', headerName: t('invoicesPage.table.invoiceDate'), render: (r) => new Date(r.invoiceDate).toLocaleDateString() },
    {
      field: 'billingPeriodStart',
      headerName: t('invoicesPage.table.billingPeriod'),
      render: (r) => `${new Date(r.billingPeriodStart).toLocaleDateString()} – ${new Date(r.billingPeriodEnd).toLocaleDateString()}`,
    },
    {
      field: 'totalAmount',
      headerName: t('invoicesPage.table.totalAmount'),
      align: 'right',
      render: (r) => `₹${r.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
    },
    { field: 'status', headerName: t('invoicesPage.table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) =>
        r.pdfPath ? (
          <Button size="small" startIcon={<DownloadIcon />} onClick={() => void downloadInvoicePdf(r)}>
            {t('invoicesPage.table.downloadPdf')}
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader title={t('invoicesPage.title')} subtitle={t('invoicesPage.subtitle')} />
      <PortalDateRangeFilter fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} />
      <DataTable columns={columns} rows={rows} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="invoices" />
    </>
  );
}
